import type {
  MemoryEntry,
  MemoryGraph,
  MemoryGraphNode,
  MemoryGraphEdge,
} from "@zworks/console-contracts";

export interface PositionedGraphNode extends MemoryGraphNode {
  x: number;
  y: number;
  radius: number;
}

/**
 * Builds a deterministic LightRAG Knowledge Graph from existing memory facts and sources.
 * Extracts high-level concepts (topics/domains) and low-level entities (tools, repos, files, constraints),
 * then stitches them together with labeled directional relationship edges.
 */
export function buildLightRagGraph(entries: MemoryEntry[], projectName: string): MemoryGraph {
  const nodesMap = new Map<string, MemoryGraphNode>();
  const edgesMap = new Map<string, MemoryGraphEdge>();

  // Add the central Project anchor concept
  const rootConceptId = "concept-project-core";
  nodesMap.set(rootConceptId, {
    id: rootConceptId,
    name: projectName || "Project Domain",
    type: "concept",
    description: "Core architectural boundaries and operating domain for this project.",
    factCount: entries.length,
    source: "Project Scope",
  });

  // Common concept clusters based on keywords
  const conceptThemes = [
    {
      id: "concept-auth",
      name: "Security & Authentication",
      keywords: ["auth", "token", "sso", "key", "secret", "google", "oidc", "credential"],
    },
    {
      id: "concept-data",
      name: "Persistence & Database",
      keywords: ["postgres", "db", "drizzle", "database", "sql", "migration", "store", "table"],
    },
    {
      id: "concept-runtime",
      name: "Worker & Container Runtime",
      keywords: ["pod", "worker", "runtime", "container", "cluster", "k3s", "docker"],
    },
    {
      id: "concept-governance",
      name: "Governance & Audit",
      keywords: ["approval", "gate", "policy", "audit", "compliance", "credit", "ledger"],
    },
    {
      id: "concept-workflow",
      name: "Workflow & Pipelines",
      keywords: ["flow", "dag", "task", "run", "step", "pipeline", "event"],
    },
  ];

  for (const theme of conceptThemes) {
    // Check if any entries match this concept
    const matchingEntries = entries.filter((e) =>
      theme.keywords.some(
        (kw) => e.body.toLowerCase().includes(kw) || (e.ref && e.ref.toLowerCase().includes(kw)),
      ),
    );
    if (matchingEntries.length > 0) {
      nodesMap.set(theme.id, {
        id: theme.id,
        name: theme.name,
        type: "concept",
        description: `High-level theme covering ${theme.name.toLowerCase()} rules and specifications.`,
        factCount: matchingEntries.length,
        source: "Semantic Classifier",
      });

      // Edge from Root Project to Theme
      const edgeId = `edge-root-${theme.id}`;
      edgesMap.set(edgeId, {
        id: edgeId,
        source: rootConceptId,
        target: theme.id,
        relation: "governs",
        weight: matchingEntries.length,
      });
    }
  }

  // Extract Entity nodes from individual entries
  entries.forEach((entry, idx) => {
    const entityId = `entity-${entry.id}`;
    // Derive a clean entity name from ref or body
    let entityName = entry.ref
      ? entry.ref
          .replace(/^repo:|^doc:/, "")
          .split("/")
          .pop() || `Item ${idx + 1}`
      : `Fact #${idx + 1}`;
    if (entityName.length > 28) {
      entityName = entityName.slice(0, 25) + "...";
    }

    nodesMap.set(entityId, {
      id: entityId,
      name: entityName,
      type: "entity",
      description: entry.body,
      factCount: entry.runsUsed > 0 ? entry.runsUsed : 1,
      source: entry.ref || "Manual Memory Fact",
    });

    // Link entity to best-matching theme or root
    let attachedThemeId = rootConceptId;
    for (const theme of conceptThemes) {
      if (
        theme.keywords.some(
          (kw) =>
            entry.body.toLowerCase().includes(kw) ||
            (entry.ref && entry.ref.toLowerCase().includes(kw)),
        )
      ) {
        attachedThemeId = theme.id;
        break;
      }
    }

    const edgeId = `edge-${attachedThemeId}-${entityId}`;
    edgesMap.set(edgeId, {
      id: edgeId,
      source: attachedThemeId,
      target: entityId,
      relation: attachedThemeId === rootConceptId ? "documents" : "specifies",
      weight: 1,
    });
  });

  return {
    nodes: Array.from(nodesMap.values()),
    edges: Array.from(edgesMap.values()),
  };
}

/**
 * Computes coordinates for nodes on the canvas.
 * Places concepts in an inner ring and entities radiating outward in an orbit.
 */
export function layoutGraphNodes(
  graph: MemoryGraph,
  canvasWidth: number,
  canvasHeight: number,
): PositionedGraphNode[] {
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  const concepts = graph.nodes.filter((n) => n.type === "concept");
  const entities = graph.nodes.filter((n) => n.type === "entity");

  const positioned: PositionedGraphNode[] = [];

  // 1. Position Concepts
  if (concepts.length === 1) {
    positioned.push({ ...concepts[0]!, x: centerX, y: centerY, radius: 42 });
  } else {
    // Root in center if present
    const root = concepts.find((c) => c.id === "concept-project-core");
    const otherConcepts = concepts.filter((c) => c.id !== "concept-project-core");

    if (root) {
      positioned.push({ ...root, x: centerX, y: centerY, radius: 46 });
    }

    const conceptRadius = Math.min(canvasWidth, canvasHeight) * 0.24;
    const angleStep = (2 * Math.PI) / Math.max(1, otherConcepts.length);

    otherConcepts.forEach((c, i) => {
      const angle = i * angleStep - Math.PI / 2;
      positioned.push({
        ...c,
        x: centerX + Math.cos(angle) * conceptRadius,
        y: centerY + Math.sin(angle) * conceptRadius,
        radius: 36,
      });
    });
  }

  // 2. Position Entities around their parent concept or outer orbit
  const entityRadius = Math.min(canvasWidth, canvasHeight) * 0.44;
  const entityAngleStep = (2 * Math.PI) / Math.max(1, entities.length);

  entities.forEach((e, i) => {
    // Find connected concept
    const edge = graph.edges.find((ed) => ed.target === e.id);
    const parent = edge ? positioned.find((p) => p.id === edge.source) : null;

    let targetX = centerX + Math.cos(i * entityAngleStep) * entityRadius;
    let targetY = centerY + Math.sin(i * entityAngleStep) * entityRadius;

    // Jitter toward parent if available
    if (parent && parent.id !== "concept-project-core") {
      const clusterAngle =
        Math.atan2(parent.y - centerY, parent.x - centerX) + ((i % 5) - 2) * 0.22;
      const clusterDist = Math.hypot(parent.x - centerX, parent.y - centerY) + 120;
      targetX = centerX + Math.cos(clusterAngle) * clusterDist;
      targetY = centerY + Math.sin(clusterAngle) * clusterDist;
    }

    positioned.push({
      ...e,
      x: targetX,
      y: targetY,
      radius: 26,
    });
  });

  return positioned;
}
